import { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import useApi from "../../../hooks/useApi";

// Los avisos de la plataforma (Backend/src/utils/avisos/catalogo.js): a
// quién se le avisa qué y con qué texto. Cada texto se edita aquí y se guarda
// al instante; los correos siguen con sus plantillas de siempre.

const GRUPOS = [
  { valor: "equipo", titulo: "Equipo admin" },
  { valor: "artista", titulo: "Artistas (incluye sellos y managers)" },
  { valor: "difusor", titulo: "Difusores" },
  { valor: "todos", titulo: "Artistas y difusores" },
];

const CANALES = { notificacion: "Notificación", whatsapp: "WhatsApp", telegram: "Telegram" };

const rellenar = (texto, datos) =>
  String(texto).replace(/\{([a-z_]+)\}/g, (todo, nombre) =>
    nombre in datos ? datos[nombre] : todo
  );

export default function Avisos({ showSnack }) {
  const { loadApi, loadingApi } = useApi();
  const [avisos, setAvisos] = useState([]);
  const [variables, setVariables] = useState({});
  const [ejemplos, setEjemplos] = useState({});
  const [editando, setEditando] = useState(null); // aviso
  const [texto, setTexto] = useState("");
  const campo = useRef(null);

  useEffect(() => {
    loadApi("admin/avisos", true, "get")
      .then((res) => {
        setAvisos(res.data.avisos || []);
        setVariables(res.data.variables || {});
        setEjemplos(res.data.ejemplos || {});
      })
      .catch((e) => showSnack(e.message, "error"));
  }, []);

  const abrir = (aviso) => {
    setTexto(aviso.texto);
    setEditando(aviso);
  };

  const actualizar = (clave, cambios) =>
    setAvisos((prev) => prev.map((a) => (a.clave === clave ? { ...a, ...cambios } : a)));

  const guardar = () =>
    loadApi(`admin/avisos/${editando.clave}`, true, "put", { texto })
      .then((res) => {
        showSnack(res.data.message);
        actualizar(editando.clave, {
          texto: res.data.texto,
          personalizado: res.data.personalizado,
        });
        setEditando(null);
      })
      .catch((e) => showSnack(e.message, "error"));

  const restablecer = () =>
    loadApi(`admin/avisos/${editando.clave}`, true, "delete")
      .then((res) => {
        showSnack(res.data.message);
        actualizar(editando.clave, { texto: res.data.texto, personalizado: false });
        setEditando(null);
      })
      .catch((e) => showSnack(e.message, "error"));

  // Inserta {variable} donde está el cursor.
  const insertar = (nombre) => {
    const input = campo.current;
    const token = `{${nombre}}`;
    if (!input) return setTexto((t) => t + token);
    const inicio = input.selectionStart ?? texto.length;
    const fin = input.selectionEnd ?? texto.length;
    const nuevo = texto.slice(0, inicio) + token + texto.slice(fin);
    setTexto(nuevo);
    requestAnimationFrame(() => {
      input.focus();
      input.setSelectionRange(inicio + token.length, inicio + token.length);
    });
  };

  const ajenas = editando
    ? [...texto.matchAll(/\{([a-z_]+)\}/g)]
        .map((m) => m[1])
        .filter((v) => !editando.variables.includes(v))
    : [];

  return (
    <Stack gap={3}>
      <Typography variant="body2" color="grayText">
        Estos son los avisos que se mandan por notificación dentro de la app,
        por WhatsApp y por Telegram (a quien los conectó; los del equipo, a
        los números dados de alta en Telegram). Los correos no cambian. Las palabras entre llaves, como {"{titulo}"},
        se reemplazan por el dato de cada envío. Los cambios se guardan al
        instante.
      </Typography>
      {GRUPOS.map((grupo) => {
        const delGrupo = avisos.filter((a) => a.grupo === grupo.valor);
        if (delGrupo.length === 0) return null;
        return (
          <Stack key={grupo.valor} gap={1}>
            <Typography variant="h6">{grupo.titulo}</Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small" sx={{ minWidth: 760 }}>
                <TableHead>
                  <TableRow sx={{ "& th": { fontWeight: 700, bgcolor: "#f5f5f5" } }}>
                    <TableCell>#</TableCell>
                    <TableCell>Aviso</TableCell>
                    <TableCell>Mensaje</TableCell>
                    <TableCell>Sale por</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {delGrupo.map((aviso) => (
                    <TableRow key={aviso.clave}>
                      <TableCell sx={{ whiteSpace: "nowrap", verticalAlign: "top" }}>
                        {aviso.codigo}
                      </TableCell>
                      <TableCell sx={{ minWidth: 180, verticalAlign: "top" }}>
                        <Typography variant="body2" fontWeight={600}>
                          {aviso.nombre}
                          {aviso.variante && ` — ${aviso.variante}`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {aviso.cuando}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ verticalAlign: "top" }}>
                        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                          {aviso.texto}
                        </Typography>
                        {aviso.personalizado && (
                          <Chip size="small" label="Personalizado" sx={{ mt: 0.5 }} />
                        )}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap", verticalAlign: "top" }}>
                        <Typography variant="caption">
                          {aviso.canales.length
                            ? aviso.canales.map((c) => CANALES[c] || c).join(" · ")
                            : "Dentro de otros avisos"}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ verticalAlign: "top" }}>
                        <Button size="small" onClick={() => abrir(aviso)}>
                          Editar
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        );
      })}

      <Dialog open={Boolean(editando)} onClose={() => setEditando(null)} fullWidth maxWidth="sm">
        {editando && (
          <>
            <DialogTitle>
              {editando.codigo} · {editando.nombre}
              {editando.variante && ` — ${editando.variante}`}
            </DialogTitle>
            <DialogContent>
              <Stack gap={2} pt={1}>
                <Typography variant="body2" color="text.secondary">
                  {editando.cuando}
                </Typography>
                <TextField
                  label="Mensaje"
                  multiline
                  minRows={3}
                  value={texto}
                  onChange={(e) => setTexto(e.target.value)}
                  inputRef={campo}
                  inputProps={{ maxLength: 1000, "aria-label": "Mensaje del aviso" }}
                  error={ajenas.length > 0}
                  helperText={
                    ajenas.length > 0
                      ? `Este aviso no tiene ${ajenas.map((v) => `{${v}}`).join(", ")}.`
                      : `${texto.length}/1000`
                  }
                />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Datos que puedes usar (toca para insertar):
                  </Typography>
                  <Stack direction="row" gap={1} flexWrap="wrap" mt={0.5}>
                    {editando.variables.map((v) => (
                      <Tooltip key={v} title={variables[v] || ""}>
                        <Chip size="small" label={`{${v}}`} onClick={() => insertar(v)} />
                      </Tooltip>
                    ))}
                  </Stack>
                </Box>
                <Box sx={{ p: 1.5, bgcolor: "rgba(242, 114, 36, 0.06)", borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Vista previa con datos de ejemplo:
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                    {rellenar(texto, ejemplos)}
                  </Typography>
                </Box>
                {texto.trim() !== editando.porDefecto && (
                  <Typography variant="caption" color="text.secondary">
                    Por defecto: {editando.porDefecto}
                  </Typography>
                )}
              </Stack>
            </DialogContent>
            <DialogActions>
              {editando.personalizado && (
                <Button
                  color="inherit"
                  onClick={restablecer}
                  sx={{ mr: "auto" }}
                  disabled={loadingApi.includes(`delete__admin/avisos/${editando.clave}`)}
                >
                  Volver al texto por defecto
                </Button>
              )}
              <Button onClick={() => setEditando(null)}>Cancelar</Button>
              <LoadingButton
                variant="contained"
                disabled={!texto.trim() || ajenas.length > 0}
                loading={loadingApi.includes(`put__admin/avisos/${editando.clave}`)}
                onClick={guardar}
              >
                Guardar
              </LoadingButton>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Stack>
  );
}
